import React, { useState, useEffect, useCallback } from 'react'
import { CCard } from '@coreui/react-pro'
import { useSelector } from 'react-redux'
import { getFavDiagnosis } from 'src/helper/backendHelper'
import _ from 'lodash'
import {
  sortDxByFilterType,
  sortDxByFavorites,
  diagnosisSearch,
} from 'src/components/common/shared/sharedDXLogic'
import Loader from '../../../../../components/common/Loader/Loader'
import CodeSearchBox from '../../../../../components/common/SearchBoxEncounter/CodeSearchBox'

import PanelDiagnosisDisplay from './PanelDiagnosisDisplay'
import { BetterToggleSwitch } from '../../../../../components/common/Toggle/ToggleSwitch'

function evaluateDXsToShow(
  searchText,
  searchResults,
  favorites,
  currentCodeTemplate,
  currentCodeTemplateResults,
) {
  if (searchText) {
    return searchResults
  } else if (currentCodeTemplate === 'Favorites') {
    return Object.values(favorites)
  } else if (currentCodeTemplate) {
    return currentCodeTemplateResults
  } else {
    // no text or filter -> default to favorites
    return Object.values(favorites)
  }
}

async function fetchDetailedTemplateData(currentCodeTemplate, callback) {
  // if favorites, we already have them locally, so don't call for them
  if (currentCodeTemplate === 'Favorites') {
    return callback([])
  }

  await getFavDiagnosis({
    template: currentCodeTemplate,
  }).then((res) => {
    if (res?.status === 'success') {
      return callback(res.results)
    }
    return callback([])
  })
}

export default function PanelDiagnosisSearchBox({ onDxToggle }) {
  const settings = useSelector((state) => state.user.settings) || []
  const provFavDX = useSelector((state) => state.user?.providerFavDX)

  const [searchText, setSearchText] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const [currentCodeTemplate, setCurrentCodeTemplate] = useState(null)
  const [currentCodeTemplateResults, setCurrentCodeTemplateResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [useAiSearch, setUseAiSearch] = useState(localStorage.getItem('dxAiSearch') === 'true')

  let displayedDxs = evaluateDXsToShow(
    searchText,
    searchResult,
    provFavDX,
    currentCodeTemplate,
    currentCodeTemplateResults,
  )

  // if there is search text, rely on DX search results
  // and don't manually filter anything
  let filterSortedDx = searchText
    ? displayedDxs
    : sortDxByFilterType(displayedDxs, settings?.sortDx)
  displayedDxs = sortDxByFavorites(filterSortedDx, provFavDX)

  const diagnosisSearchCallback = useCallback(
    _.debounce((searchText, provFavDX) => {
      setIsLoading(true)
      searchData(searchText, provFavDX)
    }, 700),
    [settings?.sortDx],
  )

  const searchData = async (text, provFavDX) => {
    const res = await diagnosisSearch({
      searchString: text,
    })

    setIsLoading(false)
    setSearchResult(res || [])
  }

  useEffect(() => {
    diagnosisSearchCallback(searchText, provFavDX)
  }, [searchText, diagnosisSearchCallback, useAiSearch])

  return (
    <CCard id={'PanelDiagnosisSearchBox'}>
      {isLoading && <Loader />}

      <div className={'dx-ai-search'}>
        <span className="ai">AI Search</span>
        <BetterToggleSwitch
          isToggledOn={useAiSearch}
          onToggleChange={(e) => {
            localStorage.setItem('dxAiSearch', e?.currentTarget?.checked)
            setUseAiSearch(e?.currentTarget?.checked)
          }}
        />
      </div>
      <CodeSearchBox
        searchText={searchText}
        setSearchText={setSearchText}
        shouldFocus={true}
        currentCodeTemplate={currentCodeTemplate}
        setCurrentCodeTemplate={(newTemplate) => {
          if (newTemplate) {
            fetchDetailedTemplateData(newTemplate, setCurrentCodeTemplateResults)
          }
          setCurrentCodeTemplate(newTemplate)
        }}
        type={'diagnosis'}
      />

      {isLoading === false && (
        <PanelDiagnosisDisplay
          dxTabList={displayedDxs}
          onDxToggle={onDxToggle}
          showFav={true}
          hideSelectAll={true}
        />
      )}
    </CCard>
  )
}
